import React, { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * BuildTogetherSection
 * ------------------------------------------------------------------
 * "Have a complex problem? Let's solve it." section. The blue info
 * bar (Response Time / Focus Areas / Meets) sits alongside a
 * "Start a conversation" button. Clicking it expands the SAME blue
 * panel in place (no modal) to reveal a booking form; submitting
 * shows a confirmation inside that same panel.
 *
 * Height animates with the CSS grid-template-rows 0fr->1fr trick, so
 * it doesn't need a hardcoded max-height guess.
 *
 * onSubmitForm receives { fullName, company, email, phone, topic,
 * details, agreeFollowUp }. It's awaited, so it can be async — a
 * thrown error shows an inline failure instead of a false "success".
 *
 * The outer background and heading/subtitle text use the site's
 * bg-main / text-primary design tokens (see src/index.css) so this
 * section switches with the site's light/dark theme toggle. The blue
 * info/form panel keeps fixed inline colors (white text on blue) —
 * that combination stays readable in both themes, so it's left as-is.
 *
 * Usage:
 *   <BuildTogetherSection />
 *   <BuildTogetherSection onSubmitForm={(data) => fetch("/api/consult", {...})} />
 * ------------------------------------------------------------------
 */

function IconClock(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function IconLayers(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m12 2 9 5-9 5-9-5 9-5z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </svg>
  );
}
function IconVideo(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m22 8-6 4 6 4V8Z" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </svg>
  );
}
function IconArrowRight(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}
function IconCheck(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function IconSpinner(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// label/value text comes from src/i18n/locales/en.json + de.json under
// "buildTogether.info.<key>" so it translates with the site language.
const INFO_ITEM_ICONS = [
  { Icon: IconClock, key: "responseTime" },
  { Icon: IconLayers, key: "focusAreas" },
  { Icon: IconVideo, key: "meets" },
];

function fieldStyle(hasError) {
  return {
    borderColor: hasError ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.35)",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#FFFFFF",
  };
}

export default function BuildTogetherSection({ onSubmitForm }) {
  const { t } = useTranslation();
  const CONSULTATION_TOPICS = Object.values(t('buildTogether.topics', { returnObjects: true }));
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    topic: "",
    details: "",
    agreeFollowUp: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const firstFieldRef = useRef(null);

  const successTimerRef = useRef(null);

  function update(field) {
    return (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((f) => ({ ...f, [field]: value }));
      setErrors((errs) => ({ ...errs, [field]: undefined }));
    };
  }

  function openForm() {
    setExpanded(true);
    setTimeout(() => firstFieldRef.current?.focus(), 350);
  }

  function collapseAndReset() {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setExpanded(false);
    setStatus("idle");
    setForm({ fullName: "", company: "", email: "", phone: "", topic: "", details: "", agreeFollowUp: false });
    setErrors({});
  }

  // auto-dismiss the success screen after 20s
  useEffect(() => {
    if (status === "success") {
      successTimerRef.current = setTimeout(() => {
        collapseAndReset();
      }, 20000);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  function validate() {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = t('buildTogether.errors.fullName');
    if (!form.email.trim()) errs.email = t('buildTogether.errors.email');
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = t('buildTogether.errors.emailInvalid');
    if (!form.topic) errs.topic = t('buildTogether.errors.topic');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      await onSubmitForm?.(form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  }

  return (
    <div className="w-full bg-bg-main ">
      <div className="mx-auto max-w-5xl px-6 py-12 text-center sm:px-10">
        <div className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: "#2563EB" }}>
         <motion.span
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-blue-600 sm:text-sm"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
          {t('buildTogether.tag')}
        </motion.span>
         
         
        </div>
        <h2 className="mx-auto mt-3 max-w-2xl text-3xl font-extrabold leading-tight text-text-primary dark:text-white sm:text-4xl">
          {t('buildTogether.headingLine1')}
        </h2>
        <h3 className="mx-auto mt-3 max-w-2xl text-3xl font-extrabold leading-tight text-text-primary dark:text-white sm:text-3xl">
          {t('buildTogether.headingLine2')}
        </h3>
        <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-text-secondary dark:text-slate-300">
          {t('buildTogether.subtitle')}
        </p>

        {/* expanding blue panel */}
        <div className="mt-10 overflow-hidden rounded-2xl" style={{ backgroundColor: "#2563EB" }}>
          {!expanded && (
            <div className="flex flex-col items-center justify-between gap-6 px-6 py-6 sm:flex-row sm:px-8">
              <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-8">
                {INFO_ITEM_ICONS.map(({ Icon, key }) => (
                  <div key={key} className="flex items-center gap-2.5 text-left">
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                    >
                      <Icon width={16} height={16} style={{ color: "#FFFFFF" }} />
                    </div>
                    <div>
                      <div className="text-[11px] font-medium" style={{ color: "rgba(255,255,255,0.92)" }}>
                        {t(`buildTogether.info.${key}.label`)}
                      </div>
                      <div className="text-sm font-semibold" style={{ color: "#FFFFFF" }}>
                        {t(`buildTogether.info.${key}.value`)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={openForm}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: "#FFFFFF", color: "#2563EB" }}
              >
                {t('buildTogether.startConversation')}
                <IconArrowRight width={16} height={16} />
              </button>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateRows: expanded ? "1fr" : "0fr",
              transition: "grid-template-rows 0.4s ease",
            }}
          >
            <div style={{ overflow: "hidden" }}>
              <div className="px-6 py-8 text-left sm:px-8">
                {status === "success" ? (
                  <div className="relative py-4 text-center">
                    <div
                      className="absolute -top-2 left-0 right-0 h-1 overflow-hidden rounded-full"
                      style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                    >
                      <div className="vizcorn-success-timer h-full" style={{ backgroundColor: "#FFFFFF" }} />
                    </div>
                    <div
                      className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                      style={{ backgroundColor: "rgba(255,255,255,0.18)" }}
                    >
                      <IconCheck width={22} height={22} style={{ color: "#FFFFFF" }} />
                    </div>
                    <h3 className="mt-4 text-lg font-bold" style={{ color: "#FFFFFF" }}>
                      {t('buildTogether.successState.thanks', { name: form.fullName.split(" ")[0] })}
                    </h3>
                    <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.8)" }}>
                      {t('buildTogether.successState.message')}
                    </p>
                    <button
                      onClick={collapseAndReset}
                      className="mt-6 rounded-full px-5 py-2.5 text-sm font-semibold"
                      style={{ backgroundColor: "#FFFFFF", color: "#2563EB" }}
                    >
                      {t('buildTogether.successState.close')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold" style={{ color: "#FFFFFF" }}>
                          {t('buildTogether.form.title')}
                        </h3>
                        <p className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>
                          {t('buildTogether.form.subtitle')}
                        </p>
                      </div>
                      <button
                        onClick={collapseAndReset}
                        aria-label="Collapse"
                        className="shrink-0 text-sm font-semibold"
                        style={{ color: "rgba(255,255,255,0.8)" }}
                      >
                        {t('buildTogether.form.cancel')}
                      </button>
                    </div>

                    <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="consultation-fullname" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t('buildTogether.form.fullName')}
                          </label>
                          <input
                            id="consultation-fullname"
                            ref={firstFieldRef}
                            type="text"
                            placeholder={t('buildTogether.form.fullNamePlaceholder')}
                            value={form.fullName}
                            onChange={update("fullName")}
                            className="w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none placeholder:text-white/50"
                            style={fieldStyle(errors.fullName)}
                          />
                          {errors.fullName && (
                            <div className="mt-1 text-xs font-medium" style={{ color: "#FEF08A" }}>
                              {errors.fullName}
                            </div>
                          )}
                        </div>
                        <div>
                          <label htmlFor="consultation-company" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t('buildTogether.form.company')}
                          </label>
                          <input
                            id="consultation-company"
                            type="text"
                            placeholder={t('buildTogether.form.companyPlaceholder')}
                            value={form.company}
                            onChange={update("company")}
                            className="w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none placeholder:text-white/50"
                            style={fieldStyle(false)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="consultation-email" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t('buildTogether.form.email')}
                          </label>
                          <input
                            id="consultation-email"
                            type="email"
                            placeholder={t('buildTogether.form.emailPlaceholder')}
                            value={form.email}
                            onChange={update("email")}
                            className="w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none placeholder:text-white/50"
                            style={fieldStyle(errors.email)}
                          />
                          {errors.email && (
                            <div className="mt-1 text-xs font-medium" style={{ color: "#FEF08A" }}>
                              {errors.email}
                            </div>
                          )}
                        </div>
                        <div>
                          <label htmlFor="consultation-phone" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t('buildTogether.form.phone')}
                          </label>
                          <input
                            id="consultation-phone"
                            type="tel"
                            placeholder={t('buildTogether.form.phonePlaceholder')}
                            value={form.phone}
                            onChange={update("phone")}
                            className="w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none placeholder:text-white/50"
                            style={fieldStyle(false)}
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="consultation-topic" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                          {t('buildTogether.form.topicLabel')}
                        </label>
                        <select
                          id="consultation-topic"
                          value={form.topic}
                          onChange={update("topic")}
                          className="w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none"
                          style={{
                            ...fieldStyle(errors.topic),
                            colorScheme: "dark",
                          }}
                        >
                          <option value="" style={{ color: "#111827" }}>
                            {t('buildTogether.form.selectTopic')}
                          </option>
                          {CONSULTATION_TOPICS.map((topic) => (
                            <option key={topic} value={topic} style={{ color: "#111827" }}>
                              {topic}
                            </option>
                          ))}
                        </select>
                        {errors.topic && (
                          <div className="mt-1 text-xs font-medium" style={{ color: "#FEF08A" }}>
                            {errors.topic}
                          </div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="consultation-details" className="mb-1.5 block text-xs font-semibold" style={{ color: "rgba(255,255,255,0.85)" }}>
                          {t('buildTogether.form.detailsLabel')}
                        </label>
                        <textarea
                          id="consultation-details"
                          value={form.details}
                          onChange={update("details")}
                          rows={3}
                          maxLength={500}
                          placeholder={t('buildTogether.form.detailsPlaceholder')}
                          className="w-full resize-none rounded-lg border px-3.5 py-2.5 text-sm outline-none placeholder:text-white/50"
                          style={fieldStyle(false)}
                        />
                        <div className="mt-1 text-right text-[11px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                          {form.details.length}/500
                        </div>
                      </div>

                      <label className="flex items-start gap-2.5 text-sm" style={{ color: "#FFFFFF" }}>
                        <input
                          type="checkbox"
                          checked={form.agreeFollowUp}
                          onChange={update("agreeFollowUp")}
                          className="mt-0.5 h-4 w-4 shrink-0"
                        />
                        <span>
                          <span className="font-semibold">{t('buildTogether.form.consentLabel')}</span>
                          <br />
                          <span className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                            {t('buildTogether.form.consentSub')}
                          </span>
                        </span>
                      </label>

                      {status === "error" && (
                        <div className="text-xs font-medium" style={{ color: "#FEF08A" }}>
                          {t('buildTogether.form.errorGeneral')}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={status === "submitting"}
                        className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: "#FFFFFF", color: "#2563EB" }}
                      >
                        {status === "submitting" && (
                          <IconSpinner width={16} height={16} className="vizcorn-spin" />
                        )}
                        {status === "submitting" ? t('buildTogether.form.submitting') : t('buildTogether.form.submit')}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes vizcorn-spin-kf { to { transform: rotate(360deg); } }
        .vizcorn-spin { animation: vizcorn-spin-kf 0.8s linear infinite; }
        @keyframes vizcorn-timer-kf { from { width: 100%; } to { width: 0%; } }
        .vizcorn-success-timer { animation: vizcorn-timer-kf 20s linear forwards; }
      `}</style>
    </div>
  );
}

/*
Wiring onSubmitForm to a real backend:

<BuildTogetherSection
  onSubmitForm={async (data) => {
    const res = await fetch("/api/consultation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error("Submission failed");
  }}
/>
*/